import { useCallback, useRef, useState } from 'react';

const MIN_GRID_FRAME_HEIGHT = 360;

// Space the page keeps below the frame: the bottom margins of the frame and
// its ancestors plus their bottom padding and borders.
function trailingSpace(node) {
    let total = 0;
    for (let element = node; element && element !== document.documentElement; element = element.parentElement) {
        const style = window.getComputedStyle(element);
        total += parseFloat(style.marginBottom) || 0;
        if (element !== node) {
            total += (parseFloat(style.paddingBottom) || 0) + (parseFloat(style.borderBottomWidth) || 0);
        }
    }
    return total;
}

/**
 * The height that lets a grid frame reach from its top edge to the bottom of
 * the window without the page scrolling: attach `ref` to the frame and size it
 * with `gridFrameHeight`. Measured when the frame mounts, when the window or
 * the page resizes, and when a hidden tab is shown again; the listeners go
 * when the frame unmounts. Null until measured (and without layout).
 */
export default function useViewportFillHeight() {
    const [availableHeight, setAvailableHeight] = useState(null);
    const detachRef = useRef(null);
    const ref = useCallback((node) => {
        detachRef.current?.();
        detachRef.current = null;
        if (!node || typeof window === 'undefined') return;
        let frame = 0;
        const measure = () => {
            frame = 0;
            const rect = node.getBoundingClientRect();
            // A hidden tab (display: none) has no box to measure.
            if (rect.width === 0 && rect.height === 0) return;
            const top = rect.top + window.scrollY;
            const next = Math.floor(window.innerHeight - top - trailingSpace(node));
            setAvailableHeight((current) => (current === next ? current : next));
        };
        const schedule = () => {
            if (!frame) frame = window.requestAnimationFrame(measure);
        };
        measure();
        window.addEventListener('resize', schedule);
        const observer = typeof ResizeObserver === 'function' ? new ResizeObserver(schedule) : null;
        // The frame itself (hidden and shown again) and the page (content
        // above the frame growing or shrinking moves its top edge).
        observer?.observe(node);
        observer?.observe(document.body);
        detachRef.current = () => {
            window.removeEventListener('resize', schedule);
            observer?.disconnect();
            if (frame) window.cancelAnimationFrame(frame);
        };
    }, []);
    return [ref, availableHeight];
}

/**
 * A grid frame's height: its content (header + rows) when that fits, else the
 * space measured down to the window's bottom edge, never below a usable
 * minimum. Before the first measurement the window height bounds it, so a long
 * list never renders thousands of pixels of rows first.
 */
export function gridFrameHeight(contentHeight, availableHeight, minHeight = MIN_GRID_FRAME_HEIGHT) {
    const fallback = typeof window === 'undefined' ? contentHeight : window.innerHeight;
    const available = Math.max(availableHeight ?? fallback, minHeight);
    return Math.min(contentHeight, available);
}
